import { scanApiV1 as scanApi } from '@deepup/apis';
import { xyToLngLat } from '@core/logic';
import type { Theme } from '@mui/material/styles';
import { TransformationReason } from '@processingResults/components';
import type { ProcessingResult } from '@processingResults/types';
import { hasUserTransformationData } from '@scans/components/Scans/helper';
import type { Scan } from '@scans/types';
import type { BoundingBox } from '@viewer3D/types';
import { DateTime } from 'luxon';

export type TransformationMode = 'user' | 'autoAlign' | 'none';

export const isScanManuallyPositionCorrected = (scan?: Scan | null): boolean => {
  if (!scan) return false;
  return Boolean(
    scan.manualPositionCorrectionReferencePoints &&
    scan.manualPositionCorrectionReferencePoints.length > 0,
  );
};

export const getScanManualPositionValue = (scan?: Scan | null): 'Yes' | 'No' | undefined => {
  if (!scan) return undefined;
  const { manualPositionCorrectionConfiguration, manualPositionCorrectionReferencePoints } = scan;

  const hasReferencePoints = Boolean(
    manualPositionCorrectionReferencePoints && manualPositionCorrectionReferencePoints.length > 0,
  );

  const isUnspecified =
    (manualPositionCorrectionConfiguration === undefined ||
      manualPositionCorrectionConfiguration === null ||
      manualPositionCorrectionConfiguration ===
        scanApi.ManualPositionCorrectionConfiguration.UNSPECIFIED ||
      (manualPositionCorrectionConfiguration as unknown) ===
        'MANUAL_POSITION_CORRECTION_CONFIGURATION_UNSPECIFIED') &&
    !hasReferencePoints;

  return isUnspecified ? undefined : hasReferencePoints ? 'Yes' : 'No';
};

/**
 * Determines the persisted/saved transformation mode based on processing result reason.
 * This represents what is currently stored in the backend ("applied" in UI).
 */
export const getPersistedTransformationMode = (
  processingResult?: ProcessingResult | null,
): TransformationMode => {
  if (!processingResult) return 'none';

  const reason = processingResult.reason;

  // AUTO_ALIGNED reason means auto-alignment was last applied
  if (reason === TransformationReason.AutoAligned) {
    return 'autoAlign';
  }

  // Explicit user transformation reasons
  if (
    reason === TransformationReason.AlignedToOtherScans ||
    reason === TransformationReason.AlignedToPhotos ||
    reason === TransformationReason.AlignedToSatelliteOrMap ||
    reason === TransformationReason.CorrectedPositionData ||
    reason === TransformationReason.CustomerRequest ||
    reason === TransformationReason.Custom
  ) {
    return 'user';
  }

  // For UNSPECIFIED, null, undefined, or any other reason, check if user transformation data exists
  return hasUserTransformationData(processingResult) ? 'user' : 'none';
};

/**
 * Checks if the manual position warning dialog should be shown when attempting to transform a scan.
 * The warning should only show when:
 * 1. "Manual Position" is "Yes" (scan was manually corrected on scanner)
 * 2. "Scan Transformation" is "Original (applied)" (persisted mode is 'none', scan is at original position)
 */
export const shouldShowManualPositionWarning = (
  scan?: Scan | null,
  processingResult?: ProcessingResult | null,
): boolean => {
  return (
    isScanManuallyPositionCorrected(scan) &&
    getPersistedTransformationMode(processingResult) === 'none'
  );
};

/**
 * Determines the helper color for a scan based on manual position correction (scanner)
 * and backoffice manual corrections.
 */
export const getScanHelperColor = (
  scan?: Scan | null,
  processingResult?: ProcessingResult | null,
  theme?: Theme,
): string => {
  if (isScanManuallyPositionCorrected(scan)) {
    return theme ? theme.palette.error.main : '';
  }

  const persistedMode = getPersistedTransformationMode(processingResult);
  const hasUserTransform =
    persistedMode === 'user' || hasUserTransformationData(processingResult || undefined);

  if (hasUserTransform) {
    return theme ? theme.palette.warning.main : '';
  }

  return theme ? theme.palette.success.main : '';
};

export const scanToDateTime = (scan: Scan): DateTime | undefined => {
  const { metadata, createdAt } = scan;
  if (metadata?.beginDate) return DateTime.fromISO(metadata.beginDate);
  if (createdAt) return DateTime.fromISO(createdAt);
  return undefined;
};

export const sortScansByCreatedDate = (a: Scan, b: Scan): number => {
  const dateA = a.metadata?.beginDate || a.createdAt;
  const dateB = b.metadata?.beginDate || b.createdAt;
  return dateA.localeCompare(dateB);
};

export const sortScansByUploadedDate = (a: Scan, b: Scan): number => {
  const dateA = a.uploadedAt;
  const dateB = b.uploadedAt;
  return dateA && dateB ? dateA.localeCompare(dateB) : -1;
};

const getScanUTMTranslation = (scan: Scan, processingResult: ProcessingResult) => {
  const texturedMesh = processingResult.texturedMeshes?.[0];
  const disableUserTransformation = scan.disableUserTransformation ?? false;
  const disableAutoAlignedPosition = scan.disableAutoAlignedPosition ?? true;

  if (disableUserTransformation && !disableAutoAlignedPosition) {
    return processingResult.autoAlignmentTranslation || texturedMesh?.mesh?.offset;
  }
  if (!disableUserTransformation && disableAutoAlignedPosition) {
    return processingResult.userTranslation || texturedMesh?.mesh?.offset;
  }
  return texturedMesh?.mesh?.offset;
};

export const scanWithinBounds = (
  bounds: BoundingBox,
  processingResults?: Record<string, ProcessingResult> | null,
): ((scan: Scan) => boolean) => {
  if (!bounds) return () => true; // Return all scans if filter mode is Bounds but no bounds property set

  const { minX, minY, maxX, maxY } = bounds;
  try {
    const { lng: lngMin, lat: latMin } = xyToLngLat({ x: minX, y: minY });
    const { lng: lngMax, lat: latMax } = xyToLngLat({ x: maxX, y: maxY });

    return (scan: Scan): boolean => {
      if (processingResults && scan.selectedProcessingResultId) {
        const processingResult = processingResults[scan.selectedProcessingResultId];
        const texturedMesh = processingResult?.texturedMeshes?.[0];
        const localBox = texturedMesh?.texture?.boundingBox;
        const translation = processingResult
          ? getScanUTMTranslation(scan, processingResult)
          : undefined;

        if (
          localBox &&
          translation &&
          typeof translation.x === 'number' &&
          typeof translation.y === 'number'
        ) {
          const scanMinX = translation.x + localBox.minX;
          const scanMaxX = translation.x + localBox.maxX;
          const scanMinY = translation.y + localBox.minY;
          const scanMaxY = translation.y + localBox.maxY;

          return scanMaxX >= minX && scanMinX <= maxX && scanMaxY >= minY && scanMinY <= maxY;
        }
      }

      const { boundingBox } = scan.metadata;
      if (!boundingBox) {
        return false;
      }

      // Use same implementation as backend:
      // https://gamedev.stackexchange.com/questions/586/what-is-the-fastest-way-to-work-out-2d-bounding-box-intersection/913#913
      return (
        boundingBox.latitudeMax >= latMin &&
        boundingBox.latitudeMin <= latMax &&
        boundingBox.longitudeMax >= lngMin &&
        boundingBox.longitudeMin <= lngMax
      );
    };
  } catch (_error) {
    return () => false;
  }
};
